import {
  ANNUAL_SECURITY_BUNDLE_FEE_DEFAULT,
  CLOVER_FEE_OVERRIDE_KEYS,
  FEE_OVERRIDE_FIELDS,
  formatFee,
  formatPercent,
  type PricingTier,
} from "@/lib/pricingTiers";
import { icPlusPricingBase } from "@/lib/sstshubRateTemplates";
import {
  defaultSalesCodeForVolume,
  portfolioPrefixForSalesCode,
} from "@/lib/salesCode";
import type { FeeOverridesForm, IcPlusPricingOverridesForm } from "./types";
import { TF } from "./TF";

export function ManualFeeAdjustmentSection({
  idPrefix,
  tier,
  monthlyVolume,
  salesCode = "",
  authFeeOverride,
  onAuthFeeOverrideChange,
  authFeeOverrideError,
  annualSecurityBundleFee,
  onAnnualSecurityBundleFeeChange,
  annualSecurityBundleFeeError,
  feeOverrides,
  onFeeOverrideChange,
  errorAt,
  icPlusPricingOverrides,
  onIcPlusPricingOverrideChange,
}: {
  idPrefix: string;
  tier: PricingTier | null;
  // Drives the volume-derived qualified-discount % placeholders below (a
  // SEPARATE SSTHUB-template lookup from `tier`, not part of PricingTier --
  // see lib/sstshubRateTemplates.ts).
  monthlyVolume: string;
  // The operator's sales-code selection ("" = Auto). Confines the qualified-
  // discount % preview to the effective sales code's portfolio schedule so it
  // matches what boards (option A). "" resolves to the volume threshold
  // default, i.e. the same side volume-only would pick.
  salesCode?: string;
  authFeeOverride: string;
  onAuthFeeOverrideChange: (v: string) => void;
  authFeeOverrideError?: string;
  // Annual security bundle fee -- persisted to our DB only, never sent to
  // CardConnect (no API field). Blank keeps the $199 tier default.
  annualSecurityBundleFee: string;
  onAnnualSecurityBundleFeeChange: (v: string) => void;
  annualSecurityBundleFeeError?: string;
  feeOverrides: FeeOverridesForm;
  onFeeOverrideChange: (key: keyof FeeOverridesForm, v: string) => void;
  errorAt?: (field: string) => string | undefined;
  icPlusPricingOverrides: IcPlusPricingOverridesForm;
  onIcPlusPricingOverrideChange: (
    key: keyof IcPlusPricingOverridesForm,
    v: string,
  ) => void;
}) {
  // Effective sales code = explicit operator selection, else the volume
  // threshold default -- mirrors resolve_sales_code so the preview follows the
  // same portfolio schedule the backend boards. null prefix -> volume-only.
  const effectiveSalesCode =
    salesCode || defaultSalesCodeForVolume(monthlyVolume);
  const icPlusBase = icPlusPricingBase(
    monthlyVolume,
    portfolioPrefixForSalesCode(effectiveSalesCode),
  );
  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-brand-text">
          Authorization, transaction &amp; miscellaneous fees{" "}
          <span className="font-normal text-brand-muted">(admin only)</span>
        </p>
        <p className="mt-0.5 text-xs text-brand-muted">
          Overrides layered on top of{" "}
          {tier ? `${tier.label}'s` : "the merchant's eventual tier's"} default
          fees. Leave any field blank to keep the tier default. Never shown to
          the merchant.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <TF
          id={`${idPrefix}.authFee`}
          label="Authorization fee"
          inputMode="decimal"
          numeric="currency"
          value={authFeeOverride}
          onChange={onAuthFeeOverrideChange}
          maxLength={7}
          placeholder={tier ? formatFee(tier.fees.authFee) : "Tier default"}
          error={authFeeOverrideError}
        />
        <TF
          id={`${idPrefix}.annualSecurityBundleFee`}
          label="Annual security bundle fee"
          inputMode="decimal"
          numeric="currency"
          value={annualSecurityBundleFee}
          onChange={onAnnualSecurityBundleFeeChange}
          maxLength={12}
          placeholder={formatFee(
            tier?.annualSecurityBundleFee ?? ANNUAL_SECURITY_BUNDLE_FEE_DEFAULT,
          )}
          hint="Saved to our records only. CardConnect has no API field for this, so it is never sent to them."
          error={annualSecurityBundleFeeError}
        />
        {FEE_OVERRIDE_FIELDS.filter(
          (field) =>
            field.key !== "authFee" &&
            !CLOVER_FEE_OVERRIDE_KEYS.includes(
              field.key as (typeof CLOVER_FEE_OVERRIDE_KEYS)[number],
            ),
        ).map((field) => {
          const id = `${idPrefix}.${field.key}`;
          const key = field.key as keyof FeeOverridesForm;
          const value = feeOverrides[key];

          if (field.kind === "pciProgram") {
            const tierDefault = tier?.fees.pciProgramCd;
            return (
              <div key={field.key}>
                <label htmlFor={id} className="label mb-1 block">
                  {field.label}
                </label>
                <select
                  id={id}
                  className="input"
                  value={value}
                  onChange={(e) => onFeeOverrideChange(key, e.target.value)}
                >
                  <option value="">
                    {tierDefault
                      ? `${tierDefault} (Tier Default)`
                      : "Tier Default"}
                  </option>
                  <option value="CONCIERGE">CONCIERGE</option>
                  <option value="ANNUAL">ANNUAL</option>
                </select>
              </div>
            );
          }

          if (field.kind === "flag") {
            const tierDefault =
              tier?.fees[
                field.key as
                  | "duesAndAssessmentsFlg"
                  | "passthruInterchgCostsFlg"
              ];
            return (
              <div key={field.key}>
                <label htmlFor={id} className="label mb-1 block">
                  {field.label}
                </label>
                <select
                  id={id}
                  className="input"
                  value={value}
                  onChange={(e) => onFeeOverrideChange(key, e.target.value)}
                >
                  <option value="">
                    {tierDefault !== undefined
                      ? `${tierDefault ? "On" : "Off"} (Tier Default)`
                      : "Tier Default"}
                  </option>
                  <option value="true">Override: On</option>
                  <option value="false">Override: Off</option>
                </select>
              </div>
            );
          }

          // kind is "currency" | "currencyString" here (pciProgram/flag
          // handled above), so the tier value is always number | string.
          const tierValue = tier?.fees[field.key] as
            | number
            | string
            | undefined;
          return (
            <TF
              key={field.key}
              id={id}
              label={field.label}
              inputMode="decimal"
              numeric="currency"
              value={value}
              onChange={(v) => onFeeOverrideChange(key, v)}
              maxLength={12}
              placeholder={
                tierValue !== undefined ? formatFee(tierValue) : "Tier default"
              }
              error={errorAt?.(id)}
            />
          );
        })}
      </div>
      <div className="border-t border-brand-border/70 pt-3">
        <p className="text-sm font-medium text-brand-text">
          Card percentage pricing{" "}
          <span className="font-normal text-brand-muted">(admin only)</span>
        </p>
        <p className="mt-0.5 text-xs text-brand-muted">
          Interchange-plus overrides for Visa, Mastercard, and Discover, sent to
          CardConnect. Leave any field blank to keep the volume-derived template
          default.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label
              htmlFor={`${idPrefix}.interchangeTypeCode`}
              className="label mb-1 block"
            >
              Interchange type code
            </label>
            <select
              id={`${idPrefix}.interchangeTypeCode`}
              className="input"
              value={icPlusPricingOverrides.interchangeTypeCode}
              onChange={(e) =>
                onIcPlusPricingOverrideChange(
                  "interchangeTypeCode",
                  e.target.value,
                )
              }
            >
              <option value="">NET (Tier Default)</option>
              <option value="GROSS">GROSS</option>
              <option value="NET">NET</option>
            </select>
          </div>
          <TF
            id={`${idPrefix}.amexOptBlueQualDiscountPct`}
            label="Amex OptBlue qual. discount %"
            inputMode="decimal"
            numeric="percent"
            value={icPlusPricingOverrides.amexOptBlueQualDiscountPct}
            onChange={(v) =>
              onIcPlusPricingOverrideChange("amexOptBlueQualDiscountPct", v)
            }
            maxLength={8}
            placeholder={
              icPlusBase
                ? formatPercent(icPlusBase.amexOptBlueQualDiscountPct)
                : "Tier default"
            }
            error={errorAt?.(`${idPrefix}.amexOptBlueQualDiscountPct`)}
          />
          <TF
            id={`${idPrefix}.discoverQualCreditDiscountPct`}
            label="Discover qual. credit discount %"
            inputMode="decimal"
            numeric="percent"
            value={icPlusPricingOverrides.discoverQualCreditDiscountPct}
            onChange={(v) =>
              onIcPlusPricingOverrideChange("discoverQualCreditDiscountPct", v)
            }
            maxLength={8}
            placeholder={
              icPlusBase
                ? formatPercent(icPlusBase.discoverQualCreditDiscountPct)
                : "Tier default"
            }
            error={errorAt?.(`${idPrefix}.discoverQualCreditDiscountPct`)}
          />
          <TF
            id={`${idPrefix}.mastercardQualCreditDiscountPct`}
            label="Mastercard qual. credit discount %"
            inputMode="decimal"
            numeric="percent"
            value={icPlusPricingOverrides.mastercardQualCreditDiscountPct}
            onChange={(v) =>
              onIcPlusPricingOverrideChange(
                "mastercardQualCreditDiscountPct",
                v,
              )
            }
            maxLength={8}
            placeholder={
              icPlusBase
                ? formatPercent(icPlusBase.mastercardQualCreditDiscountPct)
                : "Tier default"
            }
            error={errorAt?.(`${idPrefix}.mastercardQualCreditDiscountPct`)}
          />
          <TF
            id={`${idPrefix}.visaQualCreditDiscountPct`}
            label="Visa qual. credit discount %"
            inputMode="decimal"
            numeric="percent"
            value={icPlusPricingOverrides.visaQualCreditDiscountPct}
            onChange={(v) =>
              onIcPlusPricingOverrideChange("visaQualCreditDiscountPct", v)
            }
            maxLength={8}
            placeholder={
              icPlusBase
                ? formatPercent(icPlusBase.visaQualCreditDiscountPct)
                : "Tier default"
            }
            error={errorAt?.(`${idPrefix}.visaQualCreditDiscountPct`)}
          />
        </div>
      </div>
    </div>
  );
}
